import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Image,
    Keyboard,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput as RNTextInput,
    View
} from "react-native";
import { Snackbar } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { SignatureModal } from "@/components/questions/SignatureModal";
import { formatAidePhone, showScheduledAidePhone } from "@/lib/aide-contact";
import {
    fetchSupervisoryCompetencies,
    fetchSupervisoryDuties,
    fetchSupervisoryEvaluation,
    saveSupervisoryEvaluation,
    type SupervisoryCompetencyItem,
    type SupervisoryDutyItem,
    type SupervisoryEvaluationData
} from "@/lib/api";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, gradients, radius, shadows, spacing } from "@/lib/theme";

// ── Snapshot + dirty-tracking infrastructure (Foundation F1) ───────────
// Mirror of POC's pattern in PocReviewStep.tsx. The snapshot stores the
// last server-confirmed persistence shape; isDirty() value-compares local
// state against it. trim/normalize match server-side at
// visit.service.ts:2901-2902 (which collapses "" → null on save).

function normalizeStr(s: string | null | undefined): string {
    return (s ?? "").trim();
}

function arraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

interface SupervisoryServerSnapshot {
    isAidePresent: boolean | null;
    actualAideName: string;
    absenceReasonTags: string[];
    absenceReasonNote: string;
    directObservationDuties: string[];
    directObservationCompetencies: string[];
    taskRatings: Array<{ dutyName: string; rating: string }>;
    competencyRatings: Array<{ competencyName: string; rating: string }>;
    observationNotes: string;
    supervisorComments: string;
    rnSignatureData: string | null;
    caregiverSignatureData: string | null;
}

interface Props {
    visitId: string;
    sectionId: string;
    scheduledAideName?: string | null;
    /** The scheduled aide's phone (visit.caregiverPhoneNumber), shown under
     *  the name so the nurse can confirm she is evaluating the right aide. */
    scheduledAidePhone?: string | null;
    onComplete: () => void;
    onBack: () => void;
    readOnly: boolean;
    /** True on a Supervisory Visit (isSupervisoryVisitTypeName). There the visit
     *  IS the aide: an absent aide makes it a missed visit (agency fault), never
     *  a completed evaluation (D6a), so "No" sends her to Report an issue
     *  instead of the RN-only sign-off. The same step on a SOC / reassessment
     *  visit keeps the sign-off. */
    isSupervisoryVisit?: boolean;
    /** Takes her to the visit's Report an issue sheet on "Aide not here". */
    onReportAideAbsent?: () => void;
}

const ABSENCE_REASONS = [
    "Called out sick",
    "Running late",
    "No-show",
    "Already left",
    "Patient cancelled"
] as const;

function initialsFrom(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type Rating = "meets" | "needs_improvement" | "not_applicable" | null;

interface RatedDuty extends SupervisoryDutyItem {
    rating: Rating;
}

interface RatedCompetency {
    name: string;
    rating: Rating;
}

const CATEGORY_ORDER = [
    "Personal Care",
    "Toileting",
    "Nutrition",
    "Activities",
    "Treatment",
    "Household"
];

const SUB_STEPS = [
    "Aide Presence",
    "Direct Observation",
    "Task Ratings",
    "Competencies",
    "Comments",
    "Signatures"
] as const;

const MAX_DIRECT_OBS = 3;

// Returns the first incomplete sub-step (0-5) for a loaded supervisory eval.
// Lets the load effect land the supervisor where work is needed, derived from
// server state — no client-side cursor persistence required.
//
// Step 0 "aide name present" mirrors handleNext's Step 0 gate (lines ~1037-1046):
// it accepts either the server-stored actualAideName OR the scheduledAideName
// fallback. Without the fallback, the common flow (agency-scheduled aide,
// supervisor confirms without rename) would bounce back to Step 0 on every
// remount because actualAideName stays null/"" in that case.
function computeResumeSubStep(
    existing: SupervisoryEvaluationData | null,
    dutyList: SupervisoryDutyItem[],
    competencyNames: string[],
    scheduledAideName: string | null | undefined
): number {
    if (!existing) {
        return 0;
    }
    const aideName = (existing.actualAideName?.trim() ?? "") || (scheduledAideName?.trim() ?? "");
    const absentWithReason =
        existing.isAidePresent === false &&
        ((existing.absenceReasonTags?.length ?? 0) > 0 ||
            (existing.absenceReasonNote?.trim() ?? "") !== "");
    const step0Done =
        existing.isAidePresent !== null &&
        (existing.isAidePresent === true ? aideName !== "" : absentWithReason);
    if (!step0Done) {
        return 0;
    }
    // Aide-absent flow is single-screen: there are no direct-obs / ratings /
    // signature sub-steps to resume into — the RN signs on step 0 to complete.
    if (existing.isAidePresent === false) {
        return 0;
    }
    const step1Done =
        (existing.directObservationDuties?.length ?? 0) === MAX_DIRECT_OBS &&
        (existing.directObservationCompetencies?.length ?? 0) === MAX_DIRECT_OBS;
    if (!step1Done) {
        return 1;
    }
    const ratedDutyNames = new Set(existing.taskRatings.map((r) => r.dutyName));
    const step2Done = dutyList.every((d) => ratedDutyNames.has(d.dutyName));
    if (!step2Done) {
        return 2;
    }
    const ratedCompetencyNames = new Set(existing.competencyRatings.map((r) => r.competencyName));
    const step3Done = competencyNames.every((name) => ratedCompetencyNames.has(name));
    if (!step3Done) {
        return 3;
    }
    // Step 4 (Comments) is optional; treat as always done. Step 5 (Signatures)
    // gates Complete, so an everything-done eval still lands on 5.
    return 5;
}
const VIEW_MORE_LIMIT = 10;
const SHAKE_KEYFRAMES = [-6, 6, -4, 4, 0];
const SHAKE_DURATION_MS = 600;
const HIGHLIGHT_CLEAR_MS = 2500;
const AUTOSAVE_DEBOUNCE_MS = 500;
// Initial bar-height estimate before onLayout measures the actual height.
// Used as the useState seed in SupervisoryStep so first paint doesn't flicker.
const INITIAL_STICKY_BAR_HEIGHT = 81;

function MedHeader({
    title,
    subLeft,
    subRight,
    progress
}: {
    title: string;
    subLeft: string;
    subRight: string;
    progress: number;
}) {
    return (
        <View style={styles.medHeader}>
            <Text style={styles.medHeaderTitle}>{title}</Text>
            <View style={styles.medHeaderSubRow}>
                <Text style={styles.medHeaderSubLeft}>{subLeft}</Text>
                <Text style={styles.medHeaderSubRight}>{subRight}</Text>
            </View>
            <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
            </View>
        </View>
    );
}

function MedCard({
    children,
    complete,
    error,
    cardRef,
    transform
}: {
    children: React.ReactNode;
    complete?: boolean;
    error?: boolean;
    cardRef?: React.Ref<View>;
    transform?: { translateX: Animated.Value };
}) {
    if (transform) {
        return (
            <Animated.View
                ref={cardRef as React.ComponentProps<typeof Animated.View>["ref"]}
                style={[
                    styles.medCard,
                    complete && styles.medCardComplete,
                    error && styles.medCardError,
                    { transform: [{ translateX: transform.translateX }] }
                ]}
            >
                {children}
            </Animated.View>
        );
    }
    return (
        <View
            ref={cardRef}
            style={[
                styles.medCard,
                complete && styles.medCardComplete,
                error && styles.medCardError
            ]}
        >
            {children}
        </View>
    );
}

type ChipVariant = "default" | "icd" | "needs" | "na" | "viewmore";

interface MedChipProps {
    label: string;
    selected?: boolean;
    disabled?: boolean;
    variant?: ChipVariant;
    onPress: () => void;
}

const MedChip = React.memo(
    MedChipBase,
    (prev, next) =>
        // G1: ignore onPress in the comparator — inline arrows from call sites
        // allocate fresh refs per parent render, but onPress doesn't affect
        // rendering and the always-latest closure is captured at press time.
        // readOnly is read inside the call-site onPress closures and is
        // session-bound today (set at parent mount via the dispatcher's
        // visit-completion gate). If a future feature ever toggles readOnly
        // mid-session, this memo will need readOnly as an explicit prop.
        prev.label === next.label &&
        prev.selected === next.selected &&
        prev.disabled === next.disabled &&
        prev.variant === next.variant
);

function MedChipBase({ label, selected, disabled, variant = "default", onPress }: MedChipProps) {
    return (
        <Pressable
            onPress={onPress}
            style={[
                styles.chip,
                variant === "icd" && styles.chipIcd,
                variant === "viewmore" && styles.chipViewMore,
                selected && variant === "icd" && styles.chipIcdSelected,
                selected && variant === "default" && styles.chipSelected,
                selected && variant === "needs" && styles.chipNeedsSelected,
                selected && variant === "na" && styles.chipNaSelected,
                disabled && styles.chipDisabled
            ]}
        >
            {selected ? <Text style={styles.chipCheck}>✓ </Text> : null}
            <Text
                style={[
                    styles.chipText,
                    variant === "icd" && !selected && styles.chipIcdText,
                    variant === "viewmore" && styles.chipViewMoreText,
                    selected && styles.chipTextSelected
                ]}
            >
                {label}
            </Text>
        </Pressable>
    );
}

type BadgeTone = "teal" | "amber" | "grey";

function MedBadge({ label, tone }: { label: string; tone: BadgeTone }) {
    return (
        <View
            style={[
                styles.badge,
                tone === "teal" && styles.badgeTeal,
                tone === "amber" && styles.badgeAmber,
                tone === "grey" && styles.badgeGrey
            ]}
        >
            <Text
                style={[
                    styles.badgeText,
                    tone === "teal" && styles.badgeTealText,
                    tone === "amber" && styles.badgeAmberText,
                    tone === "grey" && styles.badgeGreyText
                ]}
            >
                {label}
            </Text>
        </View>
    );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return <Text style={styles.fieldLabel}>{children}</Text>;
}

/**
 * Shown on Task Ratings + Universal Competencies rows for the 3+3 items the RN
 * picked in Step 2. Eye icon + explicit text makes "you're meant to watch this
 * live, in person" clear without needing the regulatory term "direct observation".
 */
function ObserveBadge() {
    return (
        <View style={styles.observeBadge}>
            <MaterialCommunityIcons name="eye-outline" size={12} color={colors.teal600} />
            <Text style={styles.observeBadgeText}>WATCH LIVE</Text>
        </View>
    );
}

function ManualInput({
    value,
    onChangeText,
    placeholder,
    disabled
}: {
    value: string;
    onChangeText: (s: string) => void;
    placeholder?: string;
    disabled?: boolean;
}) {
    return (
        <RNTextInput
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={colors.textTertiary}
            multiline
            editable={!disabled}
            // Clinical free-text — disable iOS/Android autocorrect so a typed
            // term like "hypotension" doesn't silently become "hypertension".
            // autoCapitalize stays "sentences" so clinical paragraphs read naturally.
            autoCorrect={false}
            autoCapitalize="sentences"
            spellCheck={false}
            style={styles.manualInput}
        />
    );
}

function StickyBar({
    primaryLabel,
    onPrimary,
    disabled,
    secondaryDisabled,
    secondaryLabel,
    onSecondary
}: {
    primaryLabel: string;
    onPrimary: () => void;
    disabled?: boolean;
    // Back is gated independently of the primary action. Sharing one `disabled`
    // prop made the final-sub-step "Complete · sign to enable" state also lock
    // Back, leaving the RN no way to pop back to fix a prior step.
    secondaryDisabled?: boolean;
    secondaryLabel?: string;
    onSecondary?: () => void;
}) {
    // Container structure lifted from NarrativeStep (View + spacing.base padding
    // + flex 1:2 ratio). Visuals kept: outlined-teal Back + gradient Continue.
    const split = !!(secondaryLabel && onSecondary);
    return (
        <View style={styles.buttons}>
            {split && (
                <Pressable
                    onPress={onSecondary}
                    disabled={secondaryDisabled}
                    style={[styles.backButton, secondaryDisabled && styles.btnDisabled]}
                >
                    <Text style={styles.backButtonText}>{secondaryLabel}</Text>
                </Pressable>
            )}
            <Pressable
                onPress={onPrimary}
                disabled={disabled}
                style={split ? styles.nextButton : styles.nextButtonFull}
            >
                {disabled ? (
                    <View style={[styles.nextButtonInner, styles.nextButtonDisabledInner]}>
                        <Text style={[styles.nextButtonText, styles.nextButtonTextDisabled]}>
                            {primaryLabel}
                        </Text>
                    </View>
                ) : (
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.nextButtonInner}
                    >
                        <Text style={styles.nextButtonText}>{primaryLabel}</Text>
                    </LinearGradient>
                )}
            </Pressable>
        </View>
    );
}

export function SupervisoryStep({
    visitId,
    // Unused after removing AsyncStorage sub-step key; dispatcher still passes it.
    sectionId: _sectionId,
    scheduledAideName,
    scheduledAidePhone,
    onComplete,
    onBack,
    readOnly,
    isSupervisoryVisit = false,
    onReportAideAbsent
}: Props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [navigating, setNavigating] = useState(false);
    // D6a: on a Supervisory Visit "No, the aide isn't here" ends in Report an
    // issue, not in a completed evaluation. Read-only views of an old visit keep
    // the sign-off they were completed with.
    const aideAbsenceIsReported = isSupervisoryVisit && !readOnly && !!onReportAideAbsent;
    // Snackbar queue (U3): the previous single-slot snack lost rapid validation
    // messages. snacks[0] renders; onDismiss shifts; cap at 3 to avoid lockout.
    const [snacks, setSnacks] = useState<string[]>([]);
    const [_evaluationId, setEvaluationId] = useState<string | null>(null);

    // Wizard cursor — plain React state. DB is the source of truth for the
    // form's content; the cursor lives only in component state and resumes
    // from server data via computeResumeSubStep() in the load effect.
    const [currentSubStep, setCurrentSubStep] = useState(0);

    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const insets = useSafeAreaInsets();
    // Measured at runtime via onLayout on the StickyBar wrapper (D3). Initial
    // value matches the legacy magic-number first-paint.
    const [barHeight, setBarHeight] = useState(INITIAL_STICKY_BAR_HEIGHT);

    const [isAidePresent, setIsAidePresent] = useState<boolean | null>(null);

    // ── Aide identity (Step 1) ─────────────────────────────
    // If the visit row has no caregiver_name in the DB, kick straight into
    // rename mode so the supervisor types the name before answering Yes/No.
    const initialActualAideName = scheduledAideName?.trim() ? null : "";
    const [actualAideName, setActualAideName] = useState<string | null>(initialActualAideName);
    const [aideRenameMode, setAideRenameMode] = useState(!scheduledAideName?.trim());
    const [aideDraftName, setAideDraftName] = useState("");

    // ── Absence reason (Step 1, when No) ───────────────────
    const [absenceReasonTags, setAbsenceReasonTags] = useState<string[]>([]);
    const [absenceReasonNote, setAbsenceReasonNote] = useState("");

    const [directObsDuties, setDirectObsDuties] = useState<string[]>([]);
    const [directObsCompetencies, setDirectObsCompetencies] = useState<string[]>([]);
    const [blockAExpanded, setBlockAExpanded] = useState(false);
    const [blockBExpanded, setBlockBExpanded] = useState(false);
    const [blockAError, setBlockAError] = useState(false);
    const [blockBError, setBlockBError] = useState(false);
    const shakeA = useRef(new Animated.Value(0)).current;
    const shakeB = useRef(new Animated.Value(0)).current;

    const [duties, setDuties] = useState<RatedDuty[]>([]);

    // The agency-owned competency list for this visit (names + mandatory flag),
    // fetched on load. Replaces the former hardcoded SUPERVISORY_COMPETENCIES constant.
    const [competencyDefs, setCompetencyDefs] = useState<SupervisoryCompetencyItem[]>([]);
    const [competencies, setCompetencies] = useState<RatedCompetency[]>([]);
    // Names the agency marked "Observe Mandatory" — pre-selected and locked in Block B.
    const mandatoryCompetencySet = useMemo(
        () => new Set(competencyDefs.filter((c) => c.mandatory).map((c) => c.name)),
        [competencyDefs]
    );

    const [observationNotes, setObservationNotes] = useState("");
    const [supervisorComments, setSupervisorComments] = useState("");

    const [rnSignature, setRnSignature] = useState<string | null>(null);
    const [caregiverSignature, setCaregiverSignature] = useState<string | null>(null);
    const [sigModalVisible, setSigModalVisible] = useState<"rn" | "caregiver" | null>(null);

    const scrollRef = useRef<ScrollView>(null);
    const blockARef = useRef<View>(null);
    const blockBRef = useRef<View>(null);

    // ── Foundation refs (F1-F4) + B1 + E1 ──
    // savedSnapshotRef: last server-confirmed shape. isDirty() compares
    // against it. Set after every successful 200 — NOT optimistically.
    const savedSnapshotRef = useRef<SupervisoryServerSnapshot | null>(null);
    // actualAideNameRef: ref-mirror so handleNext's same-tick await persist()
    // can read the just-typed value (the useCallback closure still has the
    // pre-commit value). See plan D3.
    const actualAideNameRef = useRef<string | null>(actualAideName);
    actualAideNameRef.current = actualAideName;
    // sigDirtyRef: omit signature payload from autosaves until the supervisor
    // actually re-signs. Server already truthy-checks the field so undefined =
    // skip-upsert (visit.service.ts:2974, 2982).
    const sigDirtyRef = useRef<{ rn: boolean; caregiver: boolean }>({
        rn: false,
        caregiver: false
    });
    // sigTargetRef: capture which pad readSignature() was dispatched for at
    // dispatch time, so the WebView's async onOK can't route to the wrong pad
    // even if sigModalVisible state has flipped meanwhile.
    const sigTargetRef = useRef<"rn" | "caregiver" | null>(null);
    // mountedRef: every cross-async setState gates on this so persist's
    // finally / catch doesn't warn-on-unmount after Back / Complete.
    const mountedRef = useRef(true);
    // inflightPersistRef: chained-Promise mutex. Every persist() chains onto
    // whatever's in flight (serial by construction; no skip-and-drop).
    const inflightPersistRef = useRef<Promise<{ ok: boolean; error?: string }> | null>(null);
    // autosaveTimerRef: replaces the cleanup-local `t` so handleNext/Complete
    // can cancel a queued autosave before firing their own persist.
    const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Detect direction so G5 can preserve scroll on Back and reset on Forward.
    const prevSubStepRef = useRef(currentSubStep);
    const subStepScrollRef = useRef<Record<number, number>>({});
    // Shake animation handles — store so unmount can stop() (G4).
    const shakeHandlesRef = useRef<{
        a?: Animated.CompositeAnimation;
        b?: Animated.CompositeAnimation;
    }>({});

    // Mount cleanup: flip mountedRef and clear the autosave timer.
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
            shakeHandlesRef.current.a?.stop();
            shakeHandlesRef.current.b?.stop();
        };
    }, []);

    // ── Snack queue (U3) ──
    const pushSnack = useCallback((msg: string) => {
        setSnacks((prev) => {
            const next = [...prev, msg];
            return next.length > 3 ? next.slice(next.length - 3) : next;
        });
    }, []);
    // Back-compat alias: pushSnack APPENDS to a queue (cap 3) — it does NOT
    // replace the current snack the way the old single-slot setSnack did. The
    // ~16 existing setSnack(...) call sites keep working unchanged so the
    // diff stays small. New code should prefer pushSnack(...) directly so the
    // queue semantics are obvious at the call site.
    const setSnack = pushSnack;

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);

            try {
                // Run the two GETs in parallel — they're independent. Wrap the
                // eval call so a 404 (no existing eval yet) doesn't reject the
                // whole Promise.all.
                const [dutyResult, competencyResult, existing] = await Promise.all([
                    fetchSupervisoryDuties(visitId),
                    fetchSupervisoryCompetencies(visitId),
                    fetchSupervisoryEvaluation(visitId).catch(() => null)
                ]);
                if (cancelled) {
                    return;
                }
                const dutyList: RatedDuty[] = dutyResult.duties.map((d) => ({
                    ...d,
                    rating: null
                }));
                const competencyList = competencyResult.competencies;
                const competencyNames = competencyList.map((c) => c.name);
                const mandatoryNames = competencyList.filter((c) => c.mandatory).map((c) => c.name);
                setCompetencyDefs(competencyList);

                if (existing) {
                    setEvaluationId(existing.evaluationId);
                    setIsAidePresent(existing.isAidePresent);
                    // ?? [] fallbacks defend against older backends (e.g. staging
                    // before PR #537 lands) that don't return the new fields.
                    setDirectObsDuties(existing.directObservationDuties ?? []);
                    // Union mandatory names in so agency-required competencies are always
                    // selected (and locked), even if a stale saved row predates them.
                    setDirectObsCompetencies(
                        Array.from(
                            new Set([
                                ...mandatoryNames,
                                ...(existing.directObservationCompetencies ?? [])
                            ])
                        )
                    );
                    if (existing.actualAideName && existing.actualAideName.trim()) {
                        setActualAideName(existing.actualAideName);
                        actualAideNameRef.current = existing.actualAideName;
                        setAideRenameMode(false);
                    }
                    setAbsenceReasonTags(existing.absenceReasonTags ?? []);
                    setAbsenceReasonNote(existing.absenceReasonNote ?? "");

                    const ratingMap = new Map(
                        existing.taskRatings.map((r) => [r.dutyName, r.rating as Rating])
                    );
                    for (const duty of dutyList) {
                        duty.rating = ratingMap.get(duty.dutyName) ?? null;
                    }

                    const compMap = new Map(
                        existing.competencyRatings.map((r) => [
                            r.competencyName,
                            r.rating as Rating
                        ])
                    );
                    setCompetencies(
                        competencyNames.map((name) => ({
                            name,
                            rating: compMap.get(name) ?? null
                        }))
                    );

                    if (existing.notes) {
                        setObservationNotes(existing.notes.observationNotes ?? "");
                        setSupervisorComments(existing.notes.supervisorComments ?? "");
                    }

                    // Restore signatures so the "Complete Evaluation" button
                    // re-enables when the RN re-enters the step after signing.
                    setRnSignature(existing.rnSignatureData ?? null);
                    setCaregiverSignature(existing.caregiverSignatureData ?? null);

                    // Foundation F1: snapshot the server-confirmed shape so
                    // isDirty() has a baseline to compare against and the
                    // autosave doesn't fire-on-mount for unchanged loads.
                    savedSnapshotRef.current = {
                        isAidePresent: existing.isAidePresent,
                        actualAideName: normalizeStr(existing.actualAideName),
                        absenceReasonTags: existing.absenceReasonTags ?? [],
                        absenceReasonNote: normalizeStr(existing.absenceReasonNote),
                        directObservationDuties: existing.directObservationDuties ?? [],
                        directObservationCompetencies: existing.directObservationCompetencies ?? [],
                        taskRatings: dutyList
                            .filter((d) => d.rating !== null)
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            .map((d) => ({ dutyName: d.dutyName, rating: d.rating! })),
                        competencyRatings: competencyNames
                            .filter((name) => compMap.has(name) && compMap.get(name) !== null)
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            .map((name) => ({ competencyName: name, rating: compMap.get(name)! })),
                        observationNotes: normalizeStr(existing.notes?.observationNotes),
                        supervisorComments: normalizeStr(existing.notes?.supervisorComments),
                        rnSignatureData: existing.rnSignatureData ?? null,
                        caregiverSignatureData: existing.caregiverSignatureData ?? null
                    };
                } else {
                    // No prior eval row on the server — but the load DID
                    // succeed. Seed savedSnapshotRef with the empty baseline
                    // so isDirty() can detect first-time edits and the
                    // autosave + flush-on-unmount gates fire on step-0 input.
                    // Without this, a user typing the aide name + tapping
                    // Yes/No then getting OS-killed before tapping Continue
                    // would lose all step-0 progress (the snapshot-null
                    // gates would skip both autosave and the unmount flush).
                    // The snapshot-null state is now reserved for load
                    // FAILURE only.
                    // Pre-select the agency-mandatory competencies. The baseline below
                    // mirrors them so this pre-selection alone isn't treated as a dirty
                    // edit (no fire-on-mount autosave); the RN's own picks are what
                    // trigger the save.
                    setCompetencies(competencyNames.map((name) => ({ name, rating: null })));
                    setDirectObsCompetencies(mandatoryNames);
                    savedSnapshotRef.current = {
                        isAidePresent: null,
                        actualAideName: "",
                        absenceReasonTags: [],
                        absenceReasonNote: "",
                        directObservationDuties: [],
                        directObservationCompetencies: mandatoryNames,
                        taskRatings: [],
                        competencyRatings: [],
                        observationNotes: "",
                        supervisorComments: "",
                        rnSignatureData: null,
                        caregiverSignatureData: null
                    };
                }
                setDuties(dutyList);
                const resume = computeResumeSubStep(
                    existing,
                    dutyList,
                    competencyNames,
                    scheduledAideName
                );
                if (resume !== 0 && mountedRef.current) {
                    setCurrentSubStep(resume);
                }
            } catch (e) {
                if (!cancelled) {
                    setSnack(e instanceof Error ? e.message : "Failed to load supervisory data");
                    // On load failure, leave savedSnapshotRef === null.
                    // The autosave guard below skips while snapshot is null
                    // so we don't overwrite the server with default/empty
                    // state from a partially-hydrated screen.
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [visitId, scheduledAideName]);

    // ── isDirty() — value-equality vs savedSnapshotRef (F1) ──
    const isDirty = useCallback((): boolean => {
        const snap = savedSnapshotRef.current;
        if (!snap) {
            // No baseline = "not dirty" (NOT "always dirty", which would
            // trigger autosave on mount before the user touched anything).
            return false;
        }
        if (snap.isAidePresent !== isAidePresent) {
            return true;
        }
        if (snap.actualAideName !== normalizeStr(actualAideName)) {
            return true;
        }
        if (!arraysEqual(snap.absenceReasonTags, absenceReasonTags)) {
            return true;
        }
        if (snap.absenceReasonNote !== normalizeStr(absenceReasonNote)) {
            return true;
        }
        if (!arraysEqual(snap.directObservationDuties, directObsDuties)) {
            return true;
        }
        if (!arraysEqual(snap.directObservationCompetencies, directObsCompetencies)) {
            return true;
        }
        const liveTaskRatings = duties
            .filter((d) => d.rating !== null)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((d) => ({ dutyName: d.dutyName, rating: d.rating! }));
        if (snap.taskRatings.length !== liveTaskRatings.length) {
            return true;
        }
        for (let i = 0; i < liveTaskRatings.length; i++) {
            const a = liveTaskRatings[i];
            const b = snap.taskRatings[i];
            if (a.dutyName !== b.dutyName || a.rating !== b.rating) {
                return true;
            }
        }
        const liveCompRatings = competencies
            .filter((c) => c.rating !== null)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((c) => ({ competencyName: c.name, rating: c.rating! }));
        if (snap.competencyRatings.length !== liveCompRatings.length) {
            return true;
        }
        for (let i = 0; i < liveCompRatings.length; i++) {
            const a = liveCompRatings[i];
            const b = snap.competencyRatings[i];
            if (a.competencyName !== b.competencyName || a.rating !== b.rating) {
                return true;
            }
        }
        if (snap.observationNotes !== normalizeStr(observationNotes)) {
            return true;
        }
        if (snap.supervisorComments !== normalizeStr(supervisorComments)) {
            return true;
        }
        if (snap.rnSignatureData !== rnSignature) {
            return true;
        }
        if (snap.caregiverSignatureData !== caregiverSignature) {
            return true;
        }
        return false;
    }, [
        isAidePresent,
        actualAideName,
        absenceReasonTags,
        absenceReasonNote,
        directObsDuties,
        directObsCompetencies,
        duties,
        competencies,
        observationNotes,
        supervisorComments,
        rnSignature,
        caregiverSignature
    ]);

    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    // ── persist — chained-Promise mutex (F2) + ref-mirror reads (D3) + ──
    // ── signature-dirty omit (E1) ──
    //
    // Every call chains onto whatever's in flight. Serial by construction;
    // no skip-and-drop. handleNext/Complete/Back get the latest in-flight
    // save's promise (not a discarded one).
    const persist = useCallback(
        (opts: { isComplete?: boolean } = {}): Promise<{ ok: boolean; error?: string }> => {
            if (readOnly) {
                return Promise.resolve({ ok: true });
            }
            const actualSaveBody = async (): Promise<{ ok: boolean; error?: string }> => {
                if (mountedRef.current) {
                    setSaving(true);
                }
                // Capture the exact payload values used in this save so the
                // post-save snapshot matches what landed on the server (the
                // closure-captured state may have drifted during the await;
                // same drift-check shape as POC's PocReviewStep.tsx:395-407).
                const sentActualAideName = normalizeStr(actualAideNameRef.current);
                const sentAbsenceNote = normalizeStr(absenceReasonNote);
                const sentObservationNotes = normalizeStr(observationNotes);
                const sentSupervisorComments = normalizeStr(supervisorComments);
                const sentTaskRatings = duties
                    .filter((d) => d.rating !== null)
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    .map((d) => ({ dutyName: d.dutyName, rating: d.rating! }));
                const sentCompetencyRatings = competencies
                    .filter((c) => c.rating !== null)
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    .map((c) => ({ competencyName: c.name, rating: c.rating! }));
                // E1: signatures only sent when dirty. Server's truthy guards
                // at visit.service.ts:2974, 2982 skip the upsert on undefined.
                const sigRnSent = sigDirtyRef.current.rn;
                const sigCaregiverSent = sigDirtyRef.current.caregiver;
                try {
                    const result = await saveSupervisoryEvaluation(visitId, {
                        isAidePresent: isAidePresent ?? false,
                        actualAideName: sentActualAideName,
                        absenceReasonTags,
                        absenceReasonNote: sentAbsenceNote,
                        directObservationDuties: directObsDuties,
                        directObservationCompetencies: directObsCompetencies,
                        taskRatings: sentTaskRatings,
                        competencyRatings: sentCompetencyRatings,
                        observationNotes: sentObservationNotes || undefined,
                        supervisorComments: sentSupervisorComments || undefined,
                        rnSignatureData: sigRnSent ? (rnSignature ?? undefined) : undefined,
                        caregiverSignatureData: sigCaregiverSent
                            ? (caregiverSignature ?? undefined)
                            : undefined,
                        isComplete: opts.isComplete
                    });
                    if (mountedRef.current) {
                        setEvaluationId(result.evaluationId);
                    }
                    // After successful 200: clear signature-dirty flags (NOT
                    // optimistically; a 5xx must leave them set so the next
                    // save retries the signature). Then update the snapshot
                    // baseline to match what landed on the server.
                    if (sigRnSent) {
                        sigDirtyRef.current.rn = false;
                    }
                    if (sigCaregiverSent) {
                        sigDirtyRef.current.caregiver = false;
                    }
                    savedSnapshotRef.current = {
                        isAidePresent: isAidePresent ?? false,
                        actualAideName: sentActualAideName,
                        absenceReasonTags,
                        absenceReasonNote: sentAbsenceNote,
                        directObservationDuties: directObsDuties,
                        directObservationCompetencies: directObsCompetencies,
                        taskRatings: sentTaskRatings,
                        competencyRatings: sentCompetencyRatings,
                        observationNotes: sentObservationNotes,
                        supervisorComments: sentSupervisorComments,
                        rnSignatureData: sigRnSent
                            ? rnSignature
                            : (savedSnapshotRef.current?.rnSignatureData ?? null),
                        caregiverSignatureData: sigCaregiverSent
                            ? caregiverSignature
                            : (savedSnapshotRef.current?.caregiverSignatureData ?? null)
                    };
                    return { ok: true };
                } catch (e) {
                    const msg = e instanceof Error ? e.message : "Save failed";
                    return { ok: false, error: msg };
                } finally {
                    if (mountedRef.current) {
                        setSaving(false);
                    }
                }
            };
            const previous = inflightPersistRef.current ?? Promise.resolve(undefined);
            const promise = previous
                .catch(() => {
                    /* swallow upstream errors so this caller's promise isn't poisoned */
                })
                .then(() => actualSaveBody());
            inflightPersistRef.current = promise;
            return promise.finally(() => {
                if (inflightPersistRef.current === promise) {
                    inflightPersistRef.current = null;
                }
            });
        },
        [
            visitId,
            readOnly,
            isAidePresent,
            absenceReasonTags,
            absenceReasonNote,
            directObsDuties,
            directObsCompetencies,
            duties,
            competencies,
            observationNotes,
            supervisorComments,
            rnSignature,
            caregiverSignature
        ]
    );

    // Ref to the latest persist so the unmount-flush effect can read it
    // without taking persist as a dep (which would re-fire the effect).
    const persistRef = useRef(persist);
    persistRef.current = persist;

    // ── Flush-on-unmount (F4) — fire-and-forget if dirty ──
    useEffect(() => {
        return () => {
            if (isDirtyRef.current()) {
                void persistRef.current();
            }
        };
    }, []);

    // Reset scroll on Forward; restore cached offset on Back (G5).
    useEffect(() => {
        const prev = prevSubStepRef.current;
        if (currentSubStep > prev) {
            scrollRef.current?.scrollTo({ y: 0, animated: false });
        } else if (currentSubStep < prev) {
            const cached = subStepScrollRef.current[currentSubStep] ?? 0;
            scrollRef.current?.scrollTo({ y: cached, animated: false });
        }
        prevSubStepRef.current = currentSubStep;
    }, [currentSubStep]);

    // Track keyboard height so the StickyBar can ride above the keyboard and
    // the ScrollView's bottom inset can lift the focused TextInput above the
    // bar (otherwise Continue would obscure the absence note / rename / comments
    // inputs even when the keyboard is open).
    useEffect(() => {
        const showEvent = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
        const hideEvent = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
        const showSub = Keyboard.addListener(showEvent, (e) =>
            setKeyboardHeight(e.endCoordinates.height)
        );
        const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, []);

    useEffect(() => {
        if (loading || readOnly) {
            return;
        }
        // Don't autosave until we have a baseline snapshot — otherwise a
        // load-errored screen would overwrite the server with default state.
        if (savedSnapshotRef.current === null) {
            return;
        }
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }
        autosaveTimerRef.current = setTimeout(() => {
            // Skip if a save is already in flight — Foundation F2's chained
            // mutex would queue this behind, which is fine but wasteful.
            // handleNext/Complete/Back go through persist() directly, so a
            // missed autosave is never load-bearing.
            if (inflightPersistRef.current) {
                return;
            }
            // Skip if nothing has changed since the last successful save.
            if (!isDirtyRef.current()) {
                return;
            }
            void persist();
        }, AUTOSAVE_DEBOUNCE_MS);
        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
        };
    }, [
        loading,
        readOnly,
        isAidePresent,
        directObsDuties,
        directObsCompetencies,
        duties,
        competencies,
        observationNotes,
        supervisorComments,
        rnSignature,
        caregiverSignature,
        persist
    ]);

    // ── Shake animation (U4 + G4) ──
    // useNativeDriver: true (transform.translateX is native-driver
    // compatible). Handle stored in ref so the unmount effect can stop().
    const runShake = useCallback((which: "a" | "b") => {
        const anim = which === "a" ? shakeA : shakeB;
        const handle = Animated.sequence(
            SHAKE_KEYFRAMES.map((toValue) =>
                Animated.timing(anim, {
                    toValue,
                    duration: SHAKE_DURATION_MS / SHAKE_KEYFRAMES.length,
                    useNativeDriver: true
                })
            )
        );
        shakeHandlesRef.current[which] = handle;
        handle.start();
    }, []);

    const handleNext = useCallback(async () => {
        setNavigating(true);

        if (currentSubStep === 0) {
            // U1: auto-commit a typed-but-not-pressed-Use aide name so the
            // same-tick await persist() ships the typed value (not null).
            // Update both the React state and the ref-mirror — persist()
            // reads from actualAideNameRef.current so the next save sees
            // the just-typed value even before React commits the setState.
            let effectiveAideName = actualAideName;
            if (aideRenameMode && aideDraftName.trim() && !actualAideName) {
                const trimmed = aideDraftName.trim();
                setActualAideName(trimmed);
                actualAideNameRef.current = trimmed;
                setAideRenameMode(false);
                setAideDraftName("");
                effectiveAideName = trimmed;
            }

            // Aide name must be present (either pulled from DB or typed by supervisor).
            const aideName = (effectiveAideName ?? scheduledAideName ?? "").trim();
            if (!aideName) {
                setNavigating(false);
                setSnack("Enter the aide's name to continue.");
                return;
            }
            if (isAidePresent === null) {
                setNavigating(false);
                setSnack("Pick Yes or No to continue.");
                return;
            }
            // If No, require a reason (chip OR free-text).
            if (
                isAidePresent === false &&
                absenceReasonTags.length === 0 &&
                !absenceReasonNote.trim()
            ) {
                setNavigating(false);
                setSnack("Tell us why the aide isn't here.");
                return;
            }
        }

        if (currentSubStep === 1) {
            const aShort = directObsDuties.length !== MAX_DIRECT_OBS;
            const bShort = directObsCompetencies.length !== MAX_DIRECT_OBS;
            if (aShort || bShort) {
                setNavigating(false);
                if (aShort) {
                    setSnack(`Pick ${MAX_DIRECT_OBS - directObsDuties.length} more from Block A`);
                    setBlockAError(true);
                    runShake("a");
                    setTimeout(() => setBlockAError(false), HIGHLIGHT_CLEAR_MS);
                    blockARef.current?.measureInWindow((_x, y) => {
                        scrollRef.current?.scrollTo({
                            y: Math.max(0, y - 20),
                            animated: true
                        });
                    });
                } else {
                    setSnack(
                        `Pick ${MAX_DIRECT_OBS - directObsCompetencies.length} more from Block B`
                    );
                    setBlockBError(true);
                    runShake("b");
                    setTimeout(() => setBlockBError(false), HIGHLIGHT_CLEAR_MS);
                    blockBRef.current?.measureInWindow((_x, y) => {
                        scrollRef.current?.scrollTo({
                            y: Math.max(0, y - 20),
                            animated: true
                        });
                    });
                }
                return;
            }
        }

        if (currentSubStep === 2) {
            const unrated = duties.filter((d) => d.rating === null).length;
            if (unrated > 0) {
                setNavigating(false);
                setSnack(`${unrated} task(s) still need a rating`);
                return;
            }
        }

        if (currentSubStep === 3) {
            const unrated = competencies.filter((c) => c.rating === null).length;
            if (unrated > 0) {
                setNavigating(false);
                setSnack(`${unrated} competency item(s) still need a rating`);
                return;
            }
        }

        // A3: cancel any queued autosave timer so we don't fire a redundant
        // save right after this manual one. F2's mutex would chain them
        // anyway, but cancelling avoids the extra network round-trip.
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        // A2: gate on result.ok — don't advance on save failure, surface the
        // error via the snack queue.
        const result = await persist();
        if (!mountedRef.current) {
            return;
        }
        if (!result.ok) {
            setSnack(result.error ?? "Save failed — please retry");
            setNavigating(false);
            return;
        }
        setCurrentSubStep((s) => s + 1);
        setNavigating(false);
    }, [
        currentSubStep,
        isAidePresent,
        actualAideName,
        aideRenameMode,
        aideDraftName,
        scheduledAideName,
        absenceReasonTags,
        absenceReasonNote,
        directObsDuties,
        directObsCompetencies,
        duties,
        competencies,
        persist,
        runShake,
        setCurrentSubStep
    ]);

    const handleComplete = useCallback(async () => {
        if (!rnSignature) {
            setSnack("RN signature is required");
            return;
        }
        // Caregiver co-signature only when the aide was present. When absent,
        // the RN signs alone — there is no aide to co-sign.
        if (isAidePresent !== false && !caregiverSignature) {
            setSnack("Caregiver signature is required");
            return;
        }
        setNavigating(true);
        // Cancel any queued autosave timer (same rationale as handleNext).
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        const result = await persist({ isComplete: true });
        if (!mountedRef.current) {
            return;
        }
        setNavigating(false);
        if (!result.ok) {
            setSnack(result.error ?? "Save failed — please retry");
            return;
        }
        onComplete();
    }, [rnSignature, caregiverSignature, isAidePresent, persist, onComplete]);

    // D6a: save the "No" as a draft (never isComplete), then hand her to the
    // visit's Report an issue sheet. A failed save does not stop the report:
    // reaching the agency matters more than the draft.
    const handleReportAideAbsent = useCallback(async () => {
        if (!onReportAideAbsent) {
            return;
        }
        setNavigating(true);
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        await persist();
        if (!mountedRef.current) {
            return;
        }
        setNavigating(false);
        onReportAideAbsent();
    }, [persist, onReportAideAbsent]);

    const setDutyRating = useCallback((dutyName: string, rating: Rating) => {
        setDuties((prev) => prev.map((d) => (d.dutyName === dutyName ? { ...d, rating } : d)));
    }, []);

    const setCompetencyRating = useCallback((name: string, rating: Rating) => {
        setCompetencies((prev) => prev.map((c) => (c.name === name ? { ...c, rating } : c)));
    }, []);

    const dutiesByCategory = useMemo(
        () =>
            CATEGORY_ORDER.map((cat) => ({
                category: cat,
                duties: duties.filter((d) => d.category === cat)
            })).filter((g) => g.duties.length > 0),
        [duties]
    );

    const directObsDutiesSet = useMemo(() => new Set(directObsDuties), [directObsDuties]);
    const directObsCompSet = useMemo(() => new Set(directObsCompetencies), [directObsCompetencies]);

    const toggleDirectObsDuty = useCallback((dutyName: string) => {
        setDirectObsDuties((prev) => {
            if (prev.includes(dutyName)) {
                return prev.filter((d) => d !== dutyName);
            }
            if (prev.length >= MAX_DIRECT_OBS) {
                setSnack("Pick up to 3. Deselect one to swap.");
                return prev;
            }
            return [...prev, dutyName];
        });
    }, []);

    const toggleDirectObsCompetency = useCallback(
        (name: string) => {
            // Agency-mandatory competencies are locked on — they can't be deselected.
            if (mandatoryCompetencySet.has(name)) {
                setSnack("This competency is required by your agency.");
                return;
            }
            setDirectObsCompetencies((prev) => {
                if (prev.includes(name)) {
                    return prev.filter((d) => d !== name);
                }
                if (prev.length >= MAX_DIRECT_OBS) {
                    setSnack("Pick up to 3. Deselect one to swap.");
                    return prev;
                }
                return [...prev, name];
            });
        },
        [mandatoryCompetencySet, setSnack]
    );

    const markAllInCategory = useCallback((category: string) => {
        setDuties((prev) =>
            prev.map((d) =>
                d.category === category && d.rating === null
                    ? { ...d, rating: "meets" as const }
                    : d
            )
        );
    }, []);

    const markAllCompetencies = useCallback(() => {
        setCompetencies((prev) =>
            prev.map((c) => (c.rating === null ? { ...c, rating: "meets" as const } : c))
        );
    }, []);

    function renderAidePresence() {
        const aideName = actualAideName ?? scheduledAideName ?? "";
        const isRenamed = actualAideName !== null && actualAideName.trim().length > 0;
        const firstName = aideName.split(/\s+/)[0] || "the aide";

        const commitDraft = () => {
            const trimmed = aideDraftName.trim();
            if (!trimmed) {
                return;
            }
            setActualAideName(trimmed);
            setAideRenameMode(false);
            setAideDraftName("");
        };
        const cancelDraft = () => {
            // If the DB had no name and we're cancelling without a draft, stay
            // in rename mode — we have no name to fall back to.
            if (!scheduledAideName?.trim() && !isRenamed) {
                return;
            }
            setAideRenameMode(false);
            setAideDraftName("");
        };

        return (
            <View>
                {/* Identity hero card */}
                <View style={styles.identityCard}>
                    <View style={styles.identityOrbA} />
                    <View style={styles.identityOrbB} />

                    {aideRenameMode ? (
                        <View>
                            <Text style={styles.identityEyebrow}>WHO'S THE AIDE TODAY?</Text>
                            <View style={styles.identityInputPill}>
                                <MaterialCommunityIcons
                                    name="account-outline"
                                    size={18}
                                    color={colors.gray500}
                                />
                                <RNTextInput
                                    value={aideDraftName}
                                    onChangeText={setAideDraftName}
                                    placeholder="Type full name…"
                                    placeholderTextColor={colors.textTertiary}
                                    autoFocus
                                    style={styles.identityInputText}
                                    editable={!readOnly}
                                    onSubmitEditing={commitDraft}
                                    returnKeyType="done"
                                    // Proper-name input — disable autocorrect so
                                    // surnames like "Nuvola" / "Solanki" aren't rewritten.
                                    autoCorrect={false}
                                    autoCapitalize="words"
                                    spellCheck={false}
                                />
                            </View>
                            <View style={styles.identityActionRow}>
                                <Pressable
                                    onPress={cancelDraft}
                                    style={[
                                        styles.identityActionBtn,
                                        styles.identityActionBtnGhost
                                    ]}
                                >
                                    <Text style={styles.identityActionBtnGhostText}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={commitDraft}
                                    disabled={!aideDraftName.trim()}
                                    style={[
                                        styles.identityActionBtn,
                                        styles.identityActionBtnSolid,
                                        !aideDraftName.trim() && styles.identityActionBtnDisabled
                                    ]}
                                >
                                    <Text style={styles.identityActionBtnSolidText}>
                                        Use this name
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    ) : (
                        <View style={styles.identityRow}>
                            <View style={styles.identityAvatar}>
                                <Text style={styles.identityAvatarText}>
                                    {initialsFrom(aideName)}
                                </Text>
                            </View>
                            <View style={styles.identityNameCol}>
                                <Text style={styles.identityEyebrow}>
                                    {isRenamed ? "AIDE ON VISIT" : "SCHEDULED AIDE"}
                                </Text>
                                <Text style={styles.identityName} numberOfLines={1}>
                                    {aideName}
                                </Text>
                                {/* The scheduled aide's number belongs to the
                                    scheduled aide — shown only while the card
                                    still names her. See showScheduledAidePhone. */}
                                {showScheduledAidePhone(
                                    aideName,
                                    scheduledAideName,
                                    scheduledAidePhone
                                ) ? (
                                    <Text style={styles.identityPhone} selectable>
                                        {formatAidePhone(scheduledAidePhone ?? "")}
                                    </Text>
                                ) : null}
                                {!readOnly && (
                                    <Pressable
                                        onPress={() => {
                                            setAideDraftName(actualAideName ?? "");
                                            setAideRenameMode(true);
                                        }}
                                        hitSlop={6}
                                    >
                                        <Text style={styles.identityLink}>
                                            {isRenamed
                                                ? "Edit name"
                                                : `Not ${firstName}? Tap to change`}
                                        </Text>
                                    </Pressable>
                                )}
                            </View>
                        </View>
                    )}
                </View>

                {/* Question */}
                <Text style={styles.heroQuestion}>Is the aide present during this visit?</Text>
                <Text style={styles.heroHint}>
                    {aideAbsenceIsReported
                        ? "A supervisory visit needs the aide. If the aide isn't here, you'll report it instead of completing this form."
                        : "If the aide isn't here, the rest of the evaluation will be marked N/A."}
                </Text>

                {/* Yes/No ChoiceCards */}
                <View style={styles.choiceRow}>
                    <Pressable
                        onPress={() => !readOnly && setIsAidePresent(true)}
                        disabled={readOnly}
                        style={styles.choiceCardWrap}
                    >
                        {isAidePresent === true ? (
                            <LinearGradient
                                colors={[colors.teal400, colors.teal500]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={[styles.choiceCard, styles.choiceCardYesSelected]}
                            >
                                <View style={styles.choiceOrbYesSelected} />
                                <View style={styles.choiceContent}>
                                    <View style={styles.choiceIconYesSelected}>
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={20}
                                            color={colors.surface}
                                        />
                                    </View>
                                    <Text style={[styles.choiceTitle, styles.choiceTitleSelected]}>
                                        Yes
                                    </Text>
                                    <Text style={[styles.choiceSub, styles.choiceSubYesSelected]}>
                                        Aide is here
                                    </Text>
                                </View>
                            </LinearGradient>
                        ) : (
                            <View style={[styles.choiceCard, styles.choiceCardUnselected]}>
                                <View style={styles.choiceOrbYesUnselected} />
                                <View style={styles.choiceContent}>
                                    <View style={styles.choiceIconYesUnselected}>
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={20}
                                            color={colors.teal500}
                                        />
                                    </View>
                                    <Text style={styles.choiceTitle}>Yes</Text>
                                    <Text style={styles.choiceSub}>Aide is here</Text>
                                </View>
                            </View>
                        )}
                    </Pressable>

                    <Pressable
                        onPress={() => !readOnly && setIsAidePresent(false)}
                        disabled={readOnly}
                        style={styles.choiceCardWrap}
                    >
                        <View
                            style={[
                                styles.choiceCard,
                                isAidePresent === false
                                    ? styles.choiceCardNoSelected
                                    : styles.choiceCardUnselected
                            ]}
                        >
                            <View
                                style={
                                    isAidePresent === false
                                        ? styles.choiceOrbNoSelected
                                        : styles.choiceOrbNoUnselected
                                }
                            />
                            <View style={styles.choiceContent}>
                                <View
                                    style={
                                        isAidePresent === false
                                            ? styles.choiceIconNoSelected
                                            : styles.choiceIconNoUnselected
                                    }
                                >
                                    <MaterialCommunityIcons
                                        name="close"
                                        size={20}
                                        color={
                                            isAidePresent === false
                                                ? colors.warning600
                                                : colors.gray500
                                        }
                                    />
                                </View>
                                <Text style={styles.choiceTitle}>No</Text>
                                <Text
                                    style={[
                                        styles.choiceSub,
                                        isAidePresent === false && styles.choiceSubNoSelected
                                    ]}
                                >
                                    {aideAbsenceIsReported
                                        ? isAidePresent === false
                                            ? "Report it"
                                            : "Aide isn't here"
                                        : isAidePresent === false
                                          ? "Tell us why"
                                          : "Skip to comments"}
                                </Text>
                            </View>
                        </View>
                    </Pressable>
                </View>

                {/* Supervisory Visit, aide absent (D6a): not a completed evaluation
                    but a missed visit the agency settles, so Report an issue. */}
                {isAidePresent === false && aideAbsenceIsReported && (
                    <View style={styles.aideAbsentCard}>
                        <View style={styles.aideAbsentHeadRow}>
                            <MaterialCommunityIcons
                                name="account-alert-outline"
                                size={20}
                                color={colors.warning600}
                            />
                            <Text style={styles.aideAbsentTitle}>This visit needs the aide</Text>
                        </View>
                        <Text style={styles.aideAbsentText}>
                            A supervisory visit can&apos;t be done without the aide, so this form
                            can&apos;t be completed. Tap Report an issue (Aide not here): we&apos;ll
                            contact the agency and keep you posted in the chat. Once it&apos;s
                            settled, we&apos;ll let you know about travel time.
                        </Text>
                    </View>
                )}

                {/* Reason block — only when No */}
                {isAidePresent === false && !aideAbsenceIsReported && (
                    <View style={styles.reasonBlock}>
                        <Text style={styles.reasonLabel}>
                            WHY ISN'T THE AIDE HERE?
                            <Text style={styles.reasonLabelRequired}> · required</Text>
                        </Text>
                        <View style={styles.reasonChipRow}>
                            {ABSENCE_REASONS.map((reason) => {
                                const selected = absenceReasonTags.includes(reason);
                                return (
                                    <Pressable
                                        key={reason}
                                        onPress={() => {
                                            if (readOnly) {
                                                return;
                                            }
                                            setAbsenceReasonTags(selected ? [] : [reason]);
                                        }}
                                        style={[
                                            styles.reasonChip,
                                            selected && styles.reasonChipSelected
                                        ]}
                                    >
                                        {selected && <Text style={styles.reasonChipCheck}>✓ </Text>}
                                        <Text
                                            style={[
                                                styles.reasonChipText,
                                                selected && styles.reasonChipTextSelected
                                            ]}
                                        >
                                            {reason}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </View>
                        <RNTextInput
                            value={absenceReasonNote}
                            onChangeText={setAbsenceReasonNote}
                            placeholder="Add details for the patient record…"
                            placeholderTextColor={colors.textTertiary}
                            multiline
                            editable={!readOnly}
                            style={styles.reasonNote}
                        />
                    </View>
                )}

                {/* Aide absent → RN signs off here on the first screen; there are
                    no direct-observation / rating / co-signature steps to walk. */}
                {isAidePresent === false && !aideAbsenceIsReported && (
                    <MedCard complete={!!rnSignature}>
                        <Text
                            style={{
                                fontSize: 13,
                                lineHeight: 19,
                                color: colors.textSecondary,
                                marginBottom: 12
                            }}
                        >
                            I confirm the aide wasn't present during the visit.
                        </Text>
                        <View style={styles.cardHead}>
                            <Text style={styles.cardCatTitle}>RN Signature</Text>
                            <MedBadge
                                label={rnSignature ? "Signed" : "Pending"}
                                tone={rnSignature ? "teal" : "amber"}
                            />
                        </View>
                        {rnSignature ? (
                            <Image
                                {...logRocketRedactProps()}
                                source={{ uri: rnSignature }}
                                style={styles.sigImage}
                                resizeMode="contain"
                            />
                        ) : (
                            <Pressable
                                onPress={() => {
                                    if (readOnly) {
                                        return;
                                    }
                                    sigTargetRef.current = "rn";
                                    setSigModalVisible("rn");
                                }}
                                accessibilityRole="button"
                                accessibilityLabel="Sign RN signature"
                                style={styles.sigPad}
                            >
                                <Text style={styles.sigPadText}>Tap to sign</Text>
                            </Pressable>
                        )}
                    </MedCard>
                )}
            </View>
        );
    }

    function renderDirectObservation() {
        const allDutyNames = duties.map((d) => d.dutyName);
        const visibleA = blockAExpanded ? allDutyNames : allDutyNames.slice(0, VIEW_MORE_LIMIT);
        const hiddenA = Math.max(0, allDutyNames.length - VIEW_MORE_LIMIT);
        const allCompetencyNames = competencyDefs.map((c) => c.name);
        const visibleB = blockBExpanded
            ? allCompetencyNames
            : allCompetencyNames.slice(0, VIEW_MORE_LIMIT);
        const hiddenB = Math.max(0, allCompetencyNames.length - VIEW_MORE_LIMIT);

        const aLimit = directObsDuties.length >= MAX_DIRECT_OBS;
        const bLimit = directObsCompetencies.length >= MAX_DIRECT_OBS;

        return (
            <>
                <Text style={styles.simpleIntro}>
                    Please indicate 3 duties and 3 competencies you'll be observing today.
                </Text>
                <MedCard
                    cardRef={blockARef}
                    complete={directObsDuties.length === MAX_DIRECT_OBS && !blockAError}
                    error={blockAError}
                    transform={{ translateX: shakeA }}
                >
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>Block A · Duties</Text>
                        <MedBadge
                            label={`${directObsDuties.length} of ${MAX_DIRECT_OBS}`}
                            tone={directObsDuties.length === MAX_DIRECT_OBS ? "teal" : "grey"}
                        />
                    </View>
                    <FieldLabel>Pick 3 from the patient's POC</FieldLabel>
                    <View style={styles.chipRow}>
                        {visibleA.map((d) => {
                            const isSel = directObsDuties.includes(d);
                            const disabled = !isSel && aLimit;
                            const label =
                                duties.find((x) => x.dutyName === d)?.supervisoryLabel ?? d;
                            return (
                                <MedChip
                                    key={d}
                                    label={label}
                                    selected={isSel}
                                    disabled={disabled}
                                    variant="icd"
                                    onPress={() => {
                                        if (disabled) {
                                            setSnack("Pick up to 3. Deselect one to swap.");
                                            return;
                                        }
                                        if (!readOnly) {
                                            toggleDirectObsDuty(d);
                                        }
                                    }}
                                />
                            );
                        })}
                        {hiddenA > 0 && (
                            <MedChip
                                label={blockAExpanded ? "Show less" : `View more (${hiddenA})`}
                                variant="viewmore"
                                onPress={() => setBlockAExpanded((v) => !v)}
                            />
                        )}
                    </View>
                </MedCard>

                <MedCard
                    cardRef={blockBRef}
                    complete={directObsCompetencies.length === MAX_DIRECT_OBS && !blockBError}
                    error={blockBError}
                    transform={{ translateX: shakeB }}
                >
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>Block B · Universal competencies</Text>
                        <MedBadge
                            label={`${directObsCompetencies.length} of ${MAX_DIRECT_OBS}`}
                            tone={directObsCompetencies.length === MAX_DIRECT_OBS ? "teal" : "grey"}
                        />
                    </View>
                    <FieldLabel>Pick 3 to observe — agency-required items are locked in</FieldLabel>
                    <View style={styles.chipRow}>
                        {visibleB.map((c) => {
                            const isMandatory = mandatoryCompetencySet.has(c);
                            const isSel = directObsCompetencies.includes(c);
                            const disabled = !isMandatory && !isSel && bLimit;
                            return (
                                <MedChip
                                    key={c}
                                    label={isMandatory ? `${c} (Agency Mandatory)` : c}
                                    selected={isSel}
                                    disabled={disabled}
                                    variant="icd"
                                    onPress={() => {
                                        if (isMandatory) {
                                            setSnack("This competency is required by your agency.");
                                            return;
                                        }
                                        if (disabled) {
                                            setSnack("Pick up to 3. Deselect one to swap.");
                                            return;
                                        }
                                        if (!readOnly) {
                                            toggleDirectObsCompetency(c);
                                        }
                                    }}
                                />
                            );
                        })}
                        {hiddenB > 0 && (
                            <MedChip
                                label={blockBExpanded ? "Show less" : `View more (${hiddenB})`}
                                variant="viewmore"
                                onPress={() => setBlockBExpanded((v) => !v)}
                            />
                        )}
                    </View>
                </MedCard>
            </>
        );
    }

    function renderTaskRatings() {
        return (
            <>
                {dutiesByCategory.map(({ category, duties: catDuties }) => {
                    const ratedCount = catDuties.filter((d) => d.rating !== null).length;
                    const allRated = ratedCount === catDuties.length;
                    const needsCount = catDuties.filter(
                        (d) => d.rating === "needs_improvement"
                    ).length;
                    return (
                        <MedCard key={category} complete={allRated}>
                            <View style={styles.cardHead}>
                                <Text style={styles.cardCatTitle}>{category}</Text>
                                {allRated ? (
                                    <MedBadge
                                        label={
                                            needsCount > 0 ? `${needsCount} follow-up` : "All rated"
                                        }
                                        tone={needsCount > 0 ? "amber" : "teal"}
                                    />
                                ) : (
                                    <Pressable
                                        onPress={() => !readOnly && markAllInCategory(category)}
                                        style={styles.markAllPill}
                                    >
                                        <Text style={styles.markAllPillText}>Mark all</Text>
                                    </Pressable>
                                )}
                            </View>
                            {catDuties.map((duty) => {
                                const isDirectObs = directObsDutiesSet.has(duty.dutyName);
                                return (
                                    <View
                                        key={duty.dutyName}
                                        style={[
                                            styles.rateRow,
                                            isDirectObs && styles.rateRowDirectObs
                                        ]}
                                    >
                                        <View style={styles.rateRowNameRow}>
                                            <Text style={styles.rateRowName}>
                                                {duty.supervisoryLabel}
                                            </Text>
                                            {isDirectObs && <ObserveBadge />}
                                        </View>
                                        {isDirectObs && (
                                            <Text style={styles.observeHint}>
                                                You're observing the aide perform this during the
                                                visit
                                            </Text>
                                        )}
                                        <View style={styles.chipRow}>
                                            <MedChip
                                                label="Meets"
                                                selected={duty.rating === "meets"}
                                                onPress={() =>
                                                    !readOnly &&
                                                    setDutyRating(duty.dutyName, "meets")
                                                }
                                            />
                                            <MedChip
                                                label="Needs Impr."
                                                selected={duty.rating === "needs_improvement"}
                                                variant="needs"
                                                onPress={() =>
                                                    !readOnly &&
                                                    setDutyRating(
                                                        duty.dutyName,
                                                        "needs_improvement"
                                                    )
                                                }
                                            />
                                            <MedChip
                                                label="N/A"
                                                selected={duty.rating === "not_applicable"}
                                                variant="na"
                                                onPress={() =>
                                                    !readOnly &&
                                                    setDutyRating(duty.dutyName, "not_applicable")
                                                }
                                            />
                                        </View>
                                    </View>
                                );
                            })}
                        </MedCard>
                    );
                })}
            </>
        );
    }

    function renderCompetencies() {
        const ratedCount = competencies.filter((c) => c.rating !== null).length;
        const allRated = ratedCount === competencies.length;
        return (
            <MedCard complete={allRated}>
                <View style={styles.cardHead}>
                    <Text style={styles.cardCatTitle}>Universal Competencies</Text>
                    {allRated ? (
                        <MedBadge label="All rated" tone="teal" />
                    ) : (
                        <Pressable
                            onPress={() => !readOnly && markAllCompetencies()}
                            style={styles.markAllPill}
                        >
                            <Text style={styles.markAllPillText}>Mark all</Text>
                        </Pressable>
                    )}
                </View>
                {competencies.map((comp) => {
                    const isDirectObs = directObsCompSet.has(comp.name);
                    return (
                        <View
                            key={comp.name}
                            style={[styles.rateRow, isDirectObs && styles.rateRowDirectObs]}
                        >
                            <View style={styles.rateRowNameRow}>
                                <Text style={styles.rateRowName}>{comp.name}</Text>
                                {isDirectObs && <ObserveBadge />}
                            </View>
                            {isDirectObs && (
                                <Text style={styles.observeHint}>
                                    You're observing the aide perform this during the visit
                                </Text>
                            )}
                            <View style={styles.chipRow}>
                                <MedChip
                                    label="Meets"
                                    selected={comp.rating === "meets"}
                                    onPress={() =>
                                        !readOnly && setCompetencyRating(comp.name, "meets")
                                    }
                                />
                                <MedChip
                                    label="Needs Impr."
                                    selected={comp.rating === "needs_improvement"}
                                    variant="needs"
                                    onPress={() =>
                                        !readOnly &&
                                        setCompetencyRating(comp.name, "needs_improvement")
                                    }
                                />
                                <MedChip
                                    label="N/A"
                                    selected={comp.rating === "not_applicable"}
                                    variant="na"
                                    onPress={() =>
                                        !readOnly &&
                                        setCompetencyRating(comp.name, "not_applicable")
                                    }
                                />
                            </View>
                        </View>
                    );
                })}
            </MedCard>
        );
    }

    function renderComments() {
        return (
            <MedCard>
                <FieldLabel>Supervisor comments (optional)</FieldLabel>
                <ManualInput
                    value={supervisorComments}
                    onChangeText={setSupervisorComments}
                    placeholder="Any observations, recommendations, or follow-up actions…"
                    disabled={readOnly}
                />
            </MedCard>
        );
    }

    function renderSignatures() {
        return (
            <>
                <MedCard complete={!!rnSignature}>
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>RN Signature</Text>
                        <MedBadge
                            label={rnSignature ? "Signed" : "Pending"}
                            tone={rnSignature ? "teal" : "amber"}
                        />
                    </View>
                    {rnSignature ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: rnSignature }}
                            style={styles.sigImage}
                            resizeMode="contain"
                        />
                    ) : (
                        <Pressable
                            onPress={() => {
                                if (readOnly) {
                                    return;
                                }
                                // Capture target at dispatch time so the WebView's
                                // async onOK can't route to the wrong pad (B1).
                                sigTargetRef.current = "rn";
                                setSigModalVisible("rn");
                            }}
                            accessibilityRole="button"
                            accessibilityLabel="Sign RN signature"
                            style={styles.sigPad}
                        >
                            <Text style={styles.sigPadText}>Tap to sign</Text>
                        </Pressable>
                    )}
                </MedCard>

                <MedCard complete={!!caregiverSignature}>
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>Caregiver Signature</Text>
                        <MedBadge
                            label={caregiverSignature ? "Signed" : "Pending"}
                            tone={caregiverSignature ? "teal" : "amber"}
                        />
                    </View>
                    {caregiverSignature ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: caregiverSignature }}
                            style={styles.sigImage}
                            resizeMode="contain"
                        />
                    ) : (
                        <Pressable
                            onPress={() => {
                                if (readOnly) {
                                    return;
                                }
                                sigTargetRef.current = "caregiver";
                                setSigModalVisible("caregiver");
                            }}
                            accessibilityRole="button"
                            accessibilityLabel="Sign caregiver signature"
                            style={styles.sigPad}
                        >
                            <Text style={styles.sigPadText}>Tap to sign</Text>
                        </Pressable>
                    )}
                </MedCard>
            </>
        );
    }

    if (loading) {
        return (
            <View style={styles.centerContainer}>
                <ActivityIndicator size="large" color={colors.teal400} />
                <Text style={styles.loadingText}>Loading supervisory checklist…</Text>
            </View>
        );
    }

    // Aide absent → RN sign-off only (no caregiver to co-sign). Aide present →
    // both signatures, captured on the final Signatures step.
    const absentTerminal = isAidePresent === false;
    const completeReady = absentTerminal ? !!rnSignature : !!rnSignature && !!caregiverSignature;
    const isFinalStep = currentSubStep === SUB_STEPS.length - 1;
    // When the aide is absent the whole eval collapses onto step 0, which then
    // acts as the terminal "Complete" action instead of advancing with "Continue".
    const showComplete = isFinalStep || (absentTerminal && currentSubStep === 0);
    // D6a: on a Supervisory Visit that terminal action is Report an issue.
    const showReportAideAbsent = absentTerminal && currentSubStep === 0 && aideAbsenceIsReported;

    return (
        <View style={styles.container}>
            <MedHeader
                title="Supervisory Evaluation"
                subLeft={SUB_STEPS[currentSubStep]}
                subRight={`${currentSubStep + 1} of ${SUB_STEPS.length}`}
                // H1: progress reads 0% on entry, 100% on completion (was
                // (currentSubStep + 1) / SUB_STEPS.length which started at 17%).
                progress={SUB_STEPS.length > 1 ? currentSubStep / (SUB_STEPS.length - 1) : 1}
            />

            <ScrollView
                ref={scrollRef}
                style={styles.body}
                // D1: contentContainerStyle.paddingBottom replaces the iOS-only
                // contentInset prop so Android also reserves the right amount
                // of room above the StickyBar + keyboard.
                contentContainerStyle={[
                    styles.bodyContent,
                    {
                        paddingBottom:
                            barHeight + (keyboardHeight > 0 ? keyboardHeight : insets.bottom) + 16
                    }
                ]}
                onScroll={(e) => {
                    subStepScrollRef.current[currentSubStep] = e.nativeEvent.contentOffset.y;
                }}
                scrollEventThrottle={64}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "none"}
            >
                {currentSubStep === 0 && renderAidePresence()}
                {currentSubStep === 1 && renderDirectObservation()}
                {currentSubStep === 2 && renderTaskRatings()}
                {currentSubStep === 3 && renderCompetencies()}
                {currentSubStep === 4 && renderComments()}
                {currentSubStep === 5 && renderSignatures()}
            </ScrollView>

            {!readOnly && (
                <View
                    pointerEvents="box-none"
                    onLayout={(e) => setBarHeight(e.nativeEvent.layout.height)}
                    style={[
                        styles.stickyWrap,
                        { bottom: keyboardHeight > 0 ? keyboardHeight : insets.bottom }
                    ]}
                >
                    <StickyBar
                        primaryLabel={
                            showReportAideAbsent
                                ? "Report an issue"
                                : showComplete
                                  ? completeReady
                                      ? "Complete Evaluation"
                                      : "Complete Evaluation · sign to enable"
                                  : "Continue"
                        }
                        onPrimary={
                            showReportAideAbsent
                                ? handleReportAideAbsent
                                : showComplete
                                  ? handleComplete
                                  : handleNext
                        }
                        disabled={
                            navigating || (showComplete && !showReportAideAbsent && !completeReady)
                        }
                        // Back is gated only on the brief `navigating` transient
                        // (prevents double-tap during persist + pop). It stays
                        // enabled when Complete is blocked on missing signatures
                        // so the RN can pop back to fix an earlier sub-step.
                        secondaryDisabled={navigating}
                        // Back is always shown — matching every other section's
                        // persistent Back affordance. On sub-steps 1+ it pops one
                        // wizard step; on sub-step 0 (no previous step) it exits the
                        // section via onBack(), which router.back()s to the screen
                        // the user came from.
                        secondaryLabel="Back"
                        // U2: Back awaits persist() so typed-but-unsaved edits land
                        // before navigating. F2's chained mutex queues this behind
                        // any in-flight save.
                        onSecondary={async () => {
                            setNavigating(true);
                            if (autosaveTimerRef.current) {
                                clearTimeout(autosaveTimerRef.current);
                                autosaveTimerRef.current = null;
                            }
                            await persist();
                            if (!mountedRef.current) {
                                return;
                            }
                            if (currentSubStep > 0) {
                                setCurrentSubStep((s) => s - 1);
                                setNavigating(false);
                            } else {
                                // Sub-step 0: leave the section entirely.
                                onBack();
                            }
                        }}
                    />
                </View>
            )}

            <SignatureModal
                // Key per signer so the underlying SignatureCanvas WebView
                // remounts when switching pads — prevents stroke leak between
                // RN and caregiver (B1).
                key={sigModalVisible}
                visible={sigModalVisible !== null}
                title={sigModalVisible === "rn" ? "RN Signature" : "Caregiver Signature"}
                onDone={(base64) => {
                    // Route by the target captured at dispatch time (sigTargetRef),
                    // not by current sigModalVisible state — the latter may
                    // have flipped during the WebView's async readSignature.
                    const target = sigTargetRef.current;
                    if (target === "rn") {
                        setRnSignature(base64);
                        sigDirtyRef.current.rn = true;
                    } else if (target === "caregiver") {
                        setCaregiverSignature(base64);
                        sigDirtyRef.current.caregiver = true;
                    }
                    sigTargetRef.current = null;
                    setSigModalVisible(null);
                }}
                onClose={() => {
                    sigTargetRef.current = null;
                    setSigModalVisible(null);
                }}
            />

            <Snackbar
                visible={snacks.length > 0}
                onDismiss={() => setSnacks((prev) => prev.slice(1))}
                duration={2000}
                style={styles.snackbar}
                wrapperStyle={{
                    bottom: barHeight + (keyboardHeight > 0 ? keyboardHeight : insets.bottom) + 8
                }}
            >
                {snacks[0] ?? ""}
            </Snackbar>
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.gray50 },
    centerContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 32
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary
    },

    medHeader: {
        backgroundColor: colors.surface,
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    medHeaderTitle: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: -0.2
    },
    medHeaderSubRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8
    },
    medHeaderSubLeft: { fontSize: 12, fontWeight: "500", color: colors.textSecondary },
    medHeaderSubRight: { fontSize: 12, fontWeight: "700", color: colors.teal400 },
    progressTrack: {
        height: 4,
        borderRadius: 9999,
        backgroundColor: colors.gray200,
        marginTop: 8,
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        borderRadius: 9999,
        backgroundColor: colors.teal400
    },

    body: { flex: 1 },
    stickyWrap: { position: "absolute", left: 0, right: 0 },
    bodyContent: { padding: 16, paddingBottom: 32 },

    heroQuestion: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: -0.3,
        lineHeight: 28,
        marginBottom: 4
    },
    heroHint: {
        fontSize: 13,
        fontWeight: "500",
        color: colors.textSecondary,
        marginBottom: 14,
        lineHeight: 19
    },

    // ── Identity hero card ─────────────────────────────────
    identityCard: {
        borderRadius: 22,
        overflow: "hidden",
        marginBottom: 16,
        backgroundColor: colors.teal400,
        padding: 20,
        position: "relative",
        // Brand-tinted shadow
        shadowColor: colors.teal500,
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.3,
        shadowRadius: 30,
        elevation: 8
    },
    identityOrbA: {
        position: "absolute",
        top: -40,
        right: -40,
        width: 160,
        height: 160,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.10)"
    },
    identityOrbB: {
        position: "absolute",
        bottom: -50,
        left: -20,
        width: 120,
        height: 120,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.08)"
    },
    identityRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14
    },
    identityAvatar: {
        width: 60,
        height: 60,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.25)",
        borderWidth: 2,
        borderColor: "rgba(255,255,255,0.5)",
        alignItems: "center",
        justifyContent: "center"
    },
    identityAvatarText: {
        color: colors.surface,
        fontSize: 20,
        fontWeight: "800"
    },
    identityNameCol: {
        flex: 1,
        minWidth: 0
    },
    identityEyebrow: {
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 0.6,
        color: colors.surface,
        opacity: 0.9,
        textTransform: "uppercase",
        marginBottom: 4
    },
    identityName: {
        fontSize: 20,
        fontWeight: "800",
        color: colors.surface,
        lineHeight: 24
    },
    identityPhone: {
        marginTop: 4,
        color: colors.surface,
        opacity: 0.95,
        fontSize: 14,
        fontWeight: "600",
        letterSpacing: 0.3
    },
    identityLink: {
        marginTop: 6,
        color: colors.surface,
        opacity: 0.92,
        fontSize: 11,
        fontWeight: "700",
        textDecorationLine: "underline"
    },
    identityInputPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 12
    },
    identityInputText: {
        flex: 1,
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary,
        padding: 0
    },
    identityActionRow: {
        flexDirection: "row",
        gap: 8
    },
    identityActionBtn: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 9999,
        alignItems: "center",
        justifyContent: "center"
    },
    identityActionBtnGhost: {
        backgroundColor: "rgba(255,255,255,0.18)",
        borderWidth: 1.5,
        borderColor: "rgba(255,255,255,0.4)"
    },
    identityActionBtnGhostText: {
        color: colors.surface,
        fontSize: 13,
        fontWeight: "700"
    },
    identityActionBtnSolid: {
        backgroundColor: colors.surface
    },
    identityActionBtnSolidText: {
        color: colors.teal600,
        fontSize: 13,
        fontWeight: "800"
    },
    identityActionBtnDisabled: {
        opacity: 0.6
    },

    // ── ChoiceCard (Yes/No) ────────────────────────────────
    choiceRow: {
        flexDirection: "row",
        gap: 10,
        marginBottom: 4
    },
    choiceCardWrap: {
        flex: 1
    },
    choiceCard: {
        padding: 18,
        borderRadius: 18,
        overflow: "hidden",
        position: "relative",
        minHeight: 132
    },
    choiceCardUnselected: {
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.border
    },
    choiceCardYesSelected: {
        shadowColor: colors.teal400,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.35,
        shadowRadius: 28,
        elevation: 8
    },
    choiceCardNoSelected: {
        backgroundColor: colors.surface,
        borderWidth: 2,
        borderColor: colors.warning500,
        shadowColor: colors.warning500,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 24,
        elevation: 6
    },
    choiceContent: {
        position: "relative",
        zIndex: 1
    },
    choiceOrbYesSelected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.15)"
    },
    choiceOrbYesUnselected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.teal50
    },
    choiceOrbNoSelected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.warning50
    },
    choiceOrbNoUnselected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.gray100
    },
    choiceIconYesSelected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: "rgba(255,255,255,0.22)",
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconYesUnselected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconNoSelected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.warning50,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconNoUnselected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceTitle: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        marginBottom: 2
    },
    choiceTitleSelected: {
        color: colors.surface
    },
    choiceSub: {
        fontSize: 12,
        fontWeight: "600",
        color: colors.textTertiary
    },
    choiceSubYesSelected: {
        color: colors.surface,
        opacity: 0.85
    },
    choiceSubNoSelected: {
        color: colors.warning600
    },

    // ── Absence-reason block ───────────────────────────────
    reasonBlock: {
        marginTop: 16
    },
    aideAbsentCard: {
        marginTop: 16,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.warning400,
        backgroundColor: colors.warning50
    },
    aideAbsentHeadRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 6
    },
    aideAbsentTitle: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.warning600
    },
    aideAbsentText: {
        fontSize: 13,
        lineHeight: 19,
        color: colors.textPrimary
    },
    reasonLabel: {
        fontSize: 10,
        fontWeight: "700",
        color: colors.textTertiary,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        marginBottom: 8
    },
    reasonLabelRequired: {
        color: colors.error500,
        opacity: 0.9
    },
    reasonChipRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 10
    },
    reasonChip: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.gray200,
        backgroundColor: colors.surface
    },
    reasonChipSelected: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal400
    },
    reasonChipText: {
        fontSize: 12,
        fontWeight: "600",
        color: colors.gray700
    },
    reasonChipTextSelected: {
        color: colors.surface
    },
    reasonChipCheck: {
        fontSize: 12,
        fontWeight: "800",
        color: colors.surface
    },
    reasonNote: {
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        padding: 12,
        fontSize: 13,
        fontWeight: "500",
        color: colors.textPrimary,
        minHeight: 70,
        textAlignVertical: "top",
        backgroundColor: colors.surface
    },

    simpleIntro: {
        fontSize: 14,
        fontWeight: "500",
        color: colors.textSecondary,
        lineHeight: 20,
        marginBottom: 16,
        paddingHorizontal: 2
    },

    medCard: {
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: 18,
        padding: 18,
        marginBottom: 14
    },
    medCardComplete: {
        borderColor: colors.teal400
    },
    medCardError: {
        borderColor: colors.error400,
        borderLeftWidth: 3,
        borderLeftColor: colors.error500
    },

    cardHead: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
        gap: 8
    },
    cardCatTitle: {
        fontSize: 11,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        flex: 1
    },

    chipRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 13,
        paddingVertical: 7,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.gray200,
        backgroundColor: colors.surface
    },
    chipDisabled: { opacity: 0.4 },
    chipText: { fontSize: 13, fontWeight: "600", color: colors.gray700 },
    chipCheck: { fontSize: 13, fontWeight: "700", color: colors.surface },
    chipSelected: {
        backgroundColor: colors.teal400,
        borderColor: colors.teal400
    },
    chipTextSelected: { color: colors.surface },
    chipIcd: {
        backgroundColor: colors.teal50,
        borderColor: colors.teal100
    },
    chipIcdText: { color: colors.teal600 },
    chipIcdSelected: {
        backgroundColor: colors.teal400,
        borderColor: colors.teal400
    },
    chipNeedsSelected: {
        backgroundColor: colors.warning500,
        borderColor: colors.warning500
    },
    chipNaSelected: {
        backgroundColor: colors.gray500,
        borderColor: colors.gray500
    },
    chipViewMore: {
        backgroundColor: colors.surface,
        borderColor: colors.teal400
    },
    chipViewMoreText: { color: colors.teal500, fontWeight: "700" },

    badge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        flexShrink: 0
    },

    observeBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal100,
        flexShrink: 0
    },
    observeBadgeText: {
        fontSize: 10,
        fontWeight: "800",
        letterSpacing: 0.6,
        color: colors.teal600
    },
    observeHint: {
        fontSize: 12,
        fontWeight: "500",
        fontStyle: "italic",
        color: colors.teal600,
        marginTop: -2,
        marginBottom: 8
    },
    badgeTeal: { backgroundColor: colors.teal50 },
    badgeAmber: { backgroundColor: colors.warning50 },
    badgeGrey: { backgroundColor: colors.gray100 },
    badgeText: {
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.5,
        textTransform: "uppercase"
    },
    badgeTealText: { color: colors.teal600 },
    badgeAmberText: { color: colors.warning600 },
    badgeGreyText: { color: colors.gray700 },

    fieldLabel: {
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: colors.textTertiary,
        marginBottom: 6
    },

    manualInput: {
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        padding: 12,
        fontSize: 13,
        fontWeight: "500",
        color: colors.textPrimary,
        minHeight: 60,
        textAlignVertical: "top"
    },

    rateRow: {
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    rateRowDirectObs: {
        borderLeftWidth: 3,
        borderLeftColor: colors.teal400,
        paddingLeft: 10,
        marginLeft: -10
    },
    rateRowNameRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        marginBottom: 8
    },
    rateRowName: {
        flex: 1,
        fontSize: 14,
        fontWeight: "500",
        color: colors.textPrimary,
        lineHeight: 20
    },

    markAllPill: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    markAllPillText: { fontSize: 11, fontWeight: "700", color: colors.teal500 },

    sigImage: { width: "100%", height: 100, marginTop: 8 },
    sigPad: {
        marginTop: 8,
        height: 100,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center"
    },
    sigPadText: { fontSize: 13, fontWeight: "600", color: colors.textTertiary },

    // ── StickyBar (container lifted from NarrativeStep) ────
    buttons: {
        flexDirection: "row",
        padding: spacing.base,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: spacing.md,
        backgroundColor: colors.surface
    },
    backButton: {
        flex: 1,
        height: 48,
        borderRadius: radius.sm,
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center"
    },
    backButtonText: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.teal500
    },
    nextButton: {
        flex: 2
    },
    nextButtonFull: {
        flex: 1
    },
    nextButtonInner: {
        height: 48,
        borderRadius: radius.sm,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.teal
    },
    nextButtonDisabledInner: {
        backgroundColor: colors.gray200
    },
    nextButtonText: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.surface
    },
    nextButtonTextDisabled: {
        color: colors.textTertiary
    },
    btnDisabled: {
        opacity: 0.5
    },

    snackbar: { backgroundColor: colors.textPrimary }
});
